import { FEATURE_KEYS, MODULE_KEYS, OPERATION_IDS } from '../routes/ids.js';
import { INVESTMENT_DOMAIN_MODULES } from '../../modules/domain-bundles.js';
const tk = (value) => value;
const telemetry = (value) => value;
const formId = (value) => value;
const fieldId = (value) => value;
const validationRule = (value) => value;
const commandId = (value) => value;
function formMetadata(id, module, feature) {
    return {
        id: formId(id),
        schemaVersion: 1,
        module,
        feature,
        privacyClassification: 'CONFIDENTIAL',
        telemetryId: telemetry(`experience.form.${id}`),
        accessibilityLabelKey: tk(`a11y.${id}`),
        availability: 'AVAILABLE',
        owner: 'experience-registry',
    };
}
function field(id, name, overrides = {}) {
    return {
        id: fieldId(id),
        name,
        labelKey: tk(id),
        fieldType: 'text',
        value: { hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        ...overrides,
    };
}
function required(messageKey = 'validation.required') {
    return {
        rules: [{ ruleId: validationRule('required'), messageKey: tk(messageKey) }],
        validateWhen: 'submit',
    };
}
function localCancel(module, feature) {
    return {
        id: 'action.cancel',
        schemaVersion: 1,
        module,
        feature,
        privacyClassification: 'INTERNAL',
        telemetryId: telemetry('experience.action.cancel'),
        accessibilityLabelKey: tk('a11y.action.cancel'),
        availability: 'AVAILABLE',
        owner: 'experience-registry',
        labelKey: tk('action.cancel'),
        iconKey: 'cancel',
        kind: 'localUi',
        submitBehavior: 'standalone',
        intent: 'secondary',
        idempotencyRequired: false,
        successBehavior: 'NO_OP',
        errorBehavior: 'SHOW_INLINE_ERROR',
        order: 10,
        group: 'secondary',
    };
}
function submitPlaceholder(id, command, module, feature, operationId) {
    return {
        id,
        schemaVersion: 1,
        module,
        feature,
        privacyClassification: 'CONFIDENTIAL',
        telemetryId: telemetry(`experience.${id}`),
        // Submit actions use a11y.action.${id} to avoid collisions with the
        // parent form's accessibilityLabelKey (a11y.${formId}).  Ids that already
        // start with "action." are kept as-is to avoid redundant nesting.
        accessibilityLabelKey: tk(id.startsWith('action.') ? `a11y.${id}` : `a11y.action.${id}`),
        availability: 'AVAILABLE',
        owner: 'experience-registry',
        labelKey: tk('action.save'),
        iconKey: 'save',
        kind: 'command',
        ...(operationId ? { operationId } : {}),
        submitBehavior: 'form_submit',
        submitConfig: {
            validatesForm: true,
            requiresDirty: true,
            requiresValid: true,
            resetBehavior: 'toServerValues',
        },
        commandId: commandId(command),
        intent: 'primary',
        idempotencyRequired: true,
        successBehavior: 'NAVIGATE_BACK',
        successMessageKey: tk('common.saveSuccess'),
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: tk('common.error.unexpected'),
        order: 20,
        group: 'primary',
    };
}
const instrumentTypes = ['EQUITY', 'DEBT', 'MUTUAL_FUND', 'PROMISSORY_NOTE'].map((value) => ({
    value,
    labelKey: tk(`enum.instrumentType.${value}`),
}));
const instrumentStatuses = ['ACTIVE', 'INACTIVE', 'DELISTED', 'SUSPENDED'].map((value) => ({
    value,
    labelKey: tk(`enum.instrumentStatus.${value}`),
}));
function instrumentFields(mode) {
    const view = mode === 'view';
    const edit = mode !== 'view';
    const writable = (operationId) => ({
        viewOperationId: OPERATION_IDS.INSTRUMENT_GET,
        ...(edit ? { editOperationId: operationId } : {}),
    });
    const readOnly = view
        ? {
            value: {
                hiddenValuePolicy: 'unregister',
                submitBehavior: 'exclude',
                outputOnly: true,
            },
        }
        : undefined;
    // ISIN is relevant for securities (EQUITY, DEBT, MUTUAL_FUND) but not promissory notes.
    const securityInstrumentTypes = ['EQUITY', 'DEBT', 'MUTUAL_FUND'];
    // Exchange listing is relevant for EQUITY and MUTUAL_FUND only.
    const listedInstrumentTypes = ['EQUITY', 'MUTUAL_FUND'];
    return [
        field('field.data.instrument.symbol', 'symbol', {
            labelKey: tk('field.instrument.symbol'),
            ...writable(mode === 'create' ? OPERATION_IDS.INSTRUMENT_CREATE : OPERATION_IDS.INSTRUMENT_UPDATE),
            validation: mode === 'create'
                ? {
                    rules: [
                        { ruleId: validationRule('required'), messageKey: tk('validation.required') },
                        {
                            ruleId: validationRule('pattern'),
                            params: { pattern: '^[A-Z0-9]+$' },
                            messageKey: tk('validation.pattern'),
                        },
                    ],
                }
                : undefined,
            state: mode === 'edit'
                ? { readonlyCondition: { operator: 'exists', field: 'field.data.instrument.symbol' } }
                : undefined,
            value: mode === 'edit'
                ? { hiddenValuePolicy: 'unregister', submitBehavior: 'exclude' }
                : readOnly?.value,
        }),
        field('field.data.instrument.instrumentName', 'instrumentName', {
            labelKey: tk('field.instrument.instrumentName'),
            ...writable(mode === 'create' ? OPERATION_IDS.INSTRUMENT_CREATE : OPERATION_IDS.INSTRUMENT_UPDATE),
            validation: edit ? required() : undefined,
            value: readOnly?.value,
        }),
        field('field.data.instrument.instrumentType', 'instrumentType', {
            labelKey: tk('field.instrument.instrumentType'),
            ...writable(mode === 'create' ? OPERATION_IDS.INSTRUMENT_CREATE : OPERATION_IDS.INSTRUMENT_UPDATE),
            fieldType: 'select',
            options: instrumentTypes,
            value: readOnly?.value,
        }),
        ...['securityName', 'currency', 'sector'].map((name) => field(`field.data.instrument.${name}`, name, {
            labelKey: tk(`field.instrument.${name}`),
            ...(name === 'currency'
                ? {
                    fieldType: 'hidden',
                    value: { defaultValue: 'NPR', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
                }
                : {
                    ...writable(mode === 'create' ? OPERATION_IDS.INSTRUMENT_CREATE : OPERATION_IDS.INSTRUMENT_UPDATE),
                    value: readOnly?.value,
                }),
        })),
        field('field.data.instrument.isin', 'isin', {
            labelKey: tk('field.instrument.isin'),
            ...writable(mode === 'create' ? OPERATION_IDS.INSTRUMENT_CREATE : OPERATION_IDS.INSTRUMENT_UPDATE),
            value: readOnly?.value,
            state: { visibilityCondition: { operator: 'in', field: 'field.data.instrument.instrumentType', values: [...securityInstrumentTypes] } },
        }),
        field('field.data.instrument.exchange', 'exchange', {
            labelKey: tk('field.instrument.exchange'),
            ...writable(mode === 'create' ? OPERATION_IDS.INSTRUMENT_CREATE : OPERATION_IDS.INSTRUMENT_UPDATE),
            value: readOnly?.value,
            state: { visibilityCondition: { operator: 'in', field: 'field.data.instrument.instrumentType', values: [...listedInstrumentTypes] } },
        }),
        field('field.data.instrument.status', 'status', {
            labelKey: tk('field.instrument.status'),
            ...writable(mode === 'create' ? OPERATION_IDS.INSTRUMENT_CREATE : OPERATION_IDS.INSTRUMENT_UPDATE),
            fieldType: 'select',
            options: instrumentStatuses,
            value: mode === 'create' ? { defaultValue: 'ACTIVE', submitBehavior: 'include' } : readOnly?.value,
        }),
        ...(mode === 'edit'
            ? [
                field('field.data.instrument.expectedVersion', 'expectedVersion', {
                    fieldType: 'hidden',
                    value: {
                        hiddenValuePolicy: 'preserve',
                        submitBehavior: 'include',
                        retainWhenHidden: true,
                    },
                }),
            ]
            : []),
    ];
}
function instrumentForm(id, mode, command) {
    const operationId = mode === 'create' ? OPERATION_IDS.INSTRUMENT_CREATE : OPERATION_IDS.INSTRUMENT_UPDATE;
    return {
        ...formMetadata(id, MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS),
        mode,
        titleKey: tk(`form.instrument.${mode}Title`),
        fields: instrumentFields(mode),
        sections: [
            {
                id: 'section.instrument.identity',
                titleKey: tk('form.instrument.section.identity'),
                fields: [
                    'field.data.instrument.symbol',
                    'field.data.instrument.instrumentName',
                    'field.data.instrument.instrumentType',
                ].map(fieldId),
                layout: { columns: 2, gap: 16 },
            },
            {
                id: 'section.instrument.details',
                titleKey: tk('form.instrument.section.details'),
                fields: [
                    'field.data.instrument.securityName',
                    'field.data.instrument.sector',
                    'field.data.instrument.isin',
                    'field.data.instrument.exchange',
                ].map(fieldId),
                layout: { columns: 2, gap: 16 },
            },
            {
                id: 'section.instrument.status',
                titleKey: tk('form.instrument.section.status'),
                fields: ['field.data.instrument.status'].map(fieldId),
                layout: { columns: 2, gap: 16 },
            },
        ],
        actions: [
            localCancel(MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS),
            ...(command
                ? [
                    submitPlaceholder('action.submit', command, MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS),
                ]
                : []),
        ],
        submission: {
            ...(command ? { defaultCommandId: commandId(command) } : {}),
            ...(mode === 'edit' ? { conflictReload: { labelKey: tk('common.refresh') } } : {}),
            successMessageKey: tk('form.instrument.success'),
            errorMessageKey: tk('form.instrument.error'),
        },
        operationId,
    };
}
export const INSTRUMENT_CREATE_FORM = instrumentForm('form.data.instrument.create', 'create', 'cmd.data.instruments.create');
export const INSTRUMENT_EDIT_FORM = instrumentForm('form.data.instrument.edit', 'edit', 'cmd.data.instruments.update');
export const INSTRUMENT_VIEW_FORM = instrumentForm('form.data.instrument.view', 'view');
const userModule = MODULE_KEYS.ADMIN;
const userFeature = FEATURE_KEYS.ADMIN_USERS;
const roleFeature = FEATURE_KEYS.ADMIN_ROLES;
const userIdentityFields = [
    field('field.user.email', 'email', {
        fieldType: 'text',
        layout: { md: 12 },
        validation: {
            rules: [
                { ruleId: validationRule('required'), messageKey: tk('validation.required') },
                { ruleId: validationRule('email'), messageKey: tk('validation.email') },
            ],
            validateWhen: 'blur',
        },
        sensitive: { payloadBehavior: 'submit', displayBehavior: 'mask' },
        helpTextKey: tk('helpText.field.user.email'),
    }),
    field('field.user.firstName', 'firstName', {
        layout: { md: 6 },
        validation: required(),
        helpTextKey: tk('helpText.field.user.firstName'),
    }),
    field('field.user.lastName', 'lastName', {
        layout: { md: 6 },
        validation: required(),
        helpTextKey: tk('helpText.field.user.lastName'),
    }),
];
const userAssignmentFields = [
    field('field.user.roles', 'roles', {
        fieldType: 'multiselect',
        layout: { md: 12 },
        optionSource: {
            adapterId: 'experience.options.roles',
            valuePath: 'id',
            labelPath: 'name',
            allowSearch: true,
            minimumSearchLength: 1,
        },
        helpTextKey: tk('helpText.field.user.roles'),
    }),
    field('field.user.clientAccountId', 'clientAccountId', {
        fieldType: 'select',
        layout: { md: 12 },
        optionSource: {
            adapterId: 'api',
            operationId: OPERATION_IDS.ORGANIZATION_CLIENTS_LIST,
            valuePath: 'id',
            labelPath: 'fullName',
            allowSearch: true,
            minimumSearchLength: 0,
        },
        helpTextKey: tk('helpText.field.user.clientAccountId'),
    }),
];
function userSections(fields) {
    const ids = new Set(fields.map((item) => String(item.id)));
    return [
        {
            id: 'section.user.identity',
            titleKey: tk('section.user.identity'),
            fields: [
                'field.user.email',
                'field.user.firstName',
                'field.user.lastName',
                'field.user.password',
            ]
                .filter((id) => ids.has(id))
                .map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
        {
            id: 'section.user.assignment',
            titleKey: tk('section.user.assignment'),
            fields: ['field.user.roles', 'field.user.branchId', 'field.user.clientAccountId', 'field.user.status']
                .filter((id) => ids.has(id))
                .map(fieldId),
            layout: { columns: 1, gap: 16 },
        },
    ].filter((section) => section.fields.length > 0);
}
export const USER_CREATE_FORM = {
    ...formMetadata('form.admin.user.create', userModule, userFeature),
    mode: 'create',
    titleKey: tk('form.user.createTitle'),
    descriptionKey: tk('form.user.createDescription'),
    fields: [
        ...userIdentityFields,
        field('field.user.password', 'password', {
            fieldType: 'password',
            layout: { md: 12 },
            validation: {
                rules: [
                    { ruleId: validationRule('required'), messageKey: tk('validation.required') },
                    {
                        ruleId: validationRule('minLength'),
                        params: { min: 8 },
                        messageKey: tk('validation.minLength'),
                    },
                ],
                validateWhen: 'blur',
            },
            sensitive: { payloadBehavior: 'submit', displayBehavior: 'hide' },
            value: {
                hiddenValuePolicy: 'clear',
                clearWhenHidden: true,
                submitBehavior: 'include',
            },
            helpTextKey: tk('helpText.field.user.password'),
        }),
        field('field.user.branchId', 'branchId', {
            fieldType: 'select',
            layout: { md: 12 },
            optionSource: {
                adapterId: 'api',
                operationId: OPERATION_IDS.ORGANIZATION_BRANCHES_LIST,
                valuePath: 'id',
                labelPath: 'name',
                allowSearch: true,
                minimumSearchLength: 1,
            },
            helpTextKey: tk('helpText.field.user.branch'),
        }),
        ...userAssignmentFields,
    ],
    sections: userSections([
        ...userIdentityFields,
        field('field.user.password', 'password'),
        field('field.user.branchId', 'branchId'),
        ...userAssignmentFields,
    ]),
    actions: [
        localCancel(userModule, userFeature),
        submitPlaceholder('action.user.create', 'cmd.admin.organization-users.create', userModule, userFeature, OPERATION_IDS.ORGANIZATION_USERS_CREATE),
    ],
    validation: { validateWhen: 'submit' },
    submission: {
        defaultCommandId: commandId('cmd.admin.organization-users.create'),
        includeFields: [
            fieldId('field.user.email'),
            fieldId('field.user.firstName'),
            fieldId('field.user.lastName'),
            fieldId('field.user.password'),
            fieldId('field.user.roles'),
            fieldId('field.user.branchId'),
            fieldId('field.user.clientAccountId'),
        ],
        successMessageKey: tk('form.user.success'),
        errorMessageKey: tk('form.user.error'),
    },
};
export const USER_EDIT_FORM = {
    ...formMetadata('form.admin.user.edit', userModule, userFeature),
    mode: 'edit',
    titleKey: tk('form.user.editTitle'),
    fields: [
        field('field.user.expectedVersion', 'expectedVersion', {
            fieldType: 'hidden',
            value: { hiddenValuePolicy: 'preserve', submitBehavior: 'include', retainWhenHidden: true },
        }),
        field('field.user.email', 'email', {
            layout: { md: 12 },
            state: { readonlyCondition: { operator: 'exists', field: 'field.user.email' } },
            display: { readonlySubmission: 'exclude' },
            sensitive: { payloadBehavior: 'omit', displayBehavior: 'show' },
            value: { submitBehavior: 'exclude', hiddenValuePolicy: 'preserve', outputOnly: true },
            helpTextKey: tk('helpText.field.user.emailEdit'),
        }),
        field('field.user.firstName', 'firstName', { layout: { md: 6 }, validation: required(), helpTextKey: tk('helpText.field.user.firstName') }),
        field('field.user.lastName', 'lastName', { layout: { md: 6 }, validation: required(), helpTextKey: tk('helpText.field.user.lastName') }),
        ...userAssignmentFields,
        field('field.user.status', 'status', {
            fieldType: 'select',
            layout: { md: 12 },
            options: ['ACTIVE', 'INACTIVE'].map((value) => ({
                value,
                labelKey: tk(`opt.status.${value}`),
            })),
            validation: required(),
            helpTextKey: tk('helpText.field.user.status'),
        }),
    ],
    sections: userSections([
        field('field.user.email', 'email'),
        field('field.user.firstName', 'firstName'),
        field('field.user.lastName', 'lastName'),
        ...userAssignmentFields,
        field('field.user.status', 'status'),
    ]),
    actions: [
        localCancel(userModule, userFeature),
        submitPlaceholder('action.user.update', 'cmd.admin.organization-users.update', userModule, userFeature, OPERATION_IDS.ORGANIZATION_USERS_UPDATE),
    ],
    validation: { validateWhen: 'submit' },
    submission: {
        defaultCommandId: commandId('cmd.admin.organization-users.update'),
        includeFields: [
            fieldId('field.user.expectedVersion'),
            fieldId('field.user.firstName'),
            fieldId('field.user.lastName'),
            fieldId('field.user.roles'),
            fieldId('field.user.clientAccountId'),
            fieldId('field.user.status'),
        ],
        successMessageKey: tk('form.user.success'),
        errorMessageKey: tk('form.user.error'),
    },
};
export const USER_VIEW_FORM = {
    ...formMetadata('form.admin.user.view', userModule, userFeature),
    mode: 'view',
    titleKey: tk('form.user.viewTitle'),
    fields: [
        field('field.user.email', 'email', {
            layout: { md: 12 },
            sensitive: { payloadBehavior: 'omit', displayBehavior: 'show' },
            value: { submitBehavior: 'exclude', hiddenValuePolicy: 'preserve', outputOnly: true },
        }),
        field('field.user.firstName', 'firstName', {
            layout: { md: 6 },
            value: { submitBehavior: 'exclude', outputOnly: true },
        }),
        field('field.user.lastName', 'lastName', {
            layout: { md: 6 },
            value: { submitBehavior: 'exclude', outputOnly: true },
        }),
        field('field.user.status', 'status', {
            fieldType: 'select',
            layout: { md: 6 },
            options: ['ACTIVE', 'INACTIVE'].map((value) => ({
                value,
                labelKey: tk(`opt.status.${value}`),
            })),
            value: { submitBehavior: 'exclude', outputOnly: true },
        }),
        field('field.user.roles', 'roles', {
            fieldType: 'multiselect',
            layout: { md: 12 },
            value: { submitBehavior: 'exclude', outputOnly: true },
        }),
        field('field.user.branchId', 'branchId', {
            fieldType: 'select',
            layout: { md: 12 },
            value: { submitBehavior: 'exclude', outputOnly: true },
        }),
    ],
    sections: userSections([
        field('field.user.email', 'email'),
        field('field.user.firstName', 'firstName'),
        field('field.user.lastName', 'lastName'),
        field('field.user.status', 'status'),
        field('field.user.roles', 'roles'),
        field('field.user.branchId', 'branchId'),
    ]),
    actions: [localCancel(userModule, userFeature)],
    submission: {
        excludeFields: [
            fieldId('field.user.email'),
            fieldId('field.user.firstName'),
            fieldId('field.user.lastName'),
            fieldId('field.user.status'),
            fieldId('field.user.roles'),
            fieldId('field.user.branchId'),
        ],
    },
};
const roleFields = (mode) => [
    ...(mode === 'edit'
        ? [
            field('field.role.expectedVersion', 'expectedVersion', {
                fieldType: 'hidden',
                value: {
                    hiddenValuePolicy: 'preserve',
                    submitBehavior: 'include',
                    retainWhenHidden: true,
                },
            }),
        ]
        : []),
    field('field.role.name', 'name', {
        layout: { md: 12 },
        validation: mode === 'view' ? undefined : required(),
        value: mode === 'view' ? { submitBehavior: 'exclude', outputOnly: true } : undefined,
        helpTextKey: tk('helpText.field.role.name'),
    }),
    field('field.role.description', 'description', {
        fieldType: 'textarea',
        layout: { md: 12 },
        value: mode === 'view' ? { submitBehavior: 'exclude', outputOnly: true } : undefined,
        helpTextKey: tk('helpText.field.role.description'),
    }),
    // Code is auto-generated by the backend in create mode; only shown in
    // edit (read-only) and view modes.
    ...(mode !== 'create'
        ? [
            field('field.role.code', 'code', {
                fieldType: 'text',
                layout: { md: 6 },
                state: mode === 'edit'
                    ? { readonlyCondition: { operator: 'exists', field: 'field.role.code' } }
                    : undefined,
                display: mode === 'edit' ? { readonlySubmission: 'exclude' } : undefined,
                helpTextKey: tk('helpText.field.role.code'),
                value: mode === 'view'
                    ? { submitBehavior: 'exclude', outputOnly: true }
                    : { submitBehavior: 'exclude', hiddenValuePolicy: 'preserve' },
            }),
        ]
        : []),
    field('field.role.status', 'status', {
        fieldType: 'select',
        layout: { md: 6 },
        options: ['ACTIVE', 'INACTIVE'].map((value) => ({
            value,
            labelKey: tk(`opt.status.${value}`),
        })),
        validation: mode === 'view' ? undefined : required(),
        value: mode === 'view'
            ? { submitBehavior: 'exclude', outputOnly: true }
            : { defaultValue: 'ACTIVE' },
        helpTextKey: tk('helpText.field.role.status'),
    }),
    field('field.role.permissions', 'permissions', {
        fieldType: 'multiselect',
        layout: { md: 12 },
        optionSource: mode === 'view'
            ? undefined
            : {
                adapterId: 'experience.options.permissions',
                valuePath: 'value',
                labelPath: 'label',
                allowSearch: true,
                minimumSearchLength: 1,
            },
        value: mode === 'view' ? { submitBehavior: 'exclude', outputOnly: true } : undefined,
        helpTextKey: tk('helpText.field.role.permissions'),
    }),
];
function roleDescriptor(mode) {
    const id = `form.admin.role.${mode}`;
    const fields = roleFields(mode);
    const mutable = mode !== 'view';
    const fieldIdsSet = new Set(fields.map((f) => String(f.id)));
    const filterFieldIds = (ids) => ids.filter((fid) => fieldIdsSet.has(fid)).map(fieldId);
    return {
        ...formMetadata(id, userModule, roleFeature),
        mode,
        titleKey: tk(`form.role.${mode}Title`),
        fields,
        sections: [
            {
                id: 'section.role.definition',
                titleKey: tk('section.role.definition'),
                fields: filterFieldIds([
                    'field.role.name',
                    'field.role.description',
                    'field.role.code',
                    'field.role.status',
                ]),
                layout: { columns: 2, gap: 16 },
            },
            {
                id: 'section.role.permissions',
                titleKey: tk('section.role.permissions'),
                fields: filterFieldIds(['field.role.permissions']),
                layout: { columns: 1, gap: 16 },
            },
        ],
        actions: mutable
            ? [
                localCancel(userModule, roleFeature),
                submitPlaceholder(mode === 'create' ? 'action.role.create' : 'action.role.update', mode === 'create' ? 'cmd.admin.organization-roles.create' : 'cmd.admin.organization-roles.update', userModule, roleFeature, mode === 'create' ? OPERATION_IDS.ORGANIZATION_ROLES_CREATE : OPERATION_IDS.ORGANIZATION_ROLES_UPDATE),
            ]
            : [localCancel(userModule, roleFeature)],
        validation: mutable ? { validateWhen: 'submit' } : undefined,
        submission: mutable
            ? {
                defaultCommandId: commandId(mode === 'create' ? 'cmd.admin.organization-roles.create' : 'cmd.admin.organization-roles.update'),
                includeFields: fields
                    .filter((item) => item.value?.submitBehavior !== 'exclude')
                    .map((item) => item.id),
                successMessageKey: tk('form.role.success'),
                errorMessageKey: tk('form.role.error'),
                ...(mode === 'edit' ? { conflictReload: { labelKey: tk('common.refresh') } } : {}),
            }
            : { excludeFields: fields.map((item) => item.id) },
    };
}
export const ROLE_CREATE_FORM = roleDescriptor('create');
export const ROLE_EDIT_FORM = roleDescriptor('edit');
export const ROLE_VIEW_FORM = roleDescriptor('view');
const transactionModule = MODULE_KEYS.TRANSACTIONS;
const transactionFeature = FEATURE_KEYS.TRANSACTIONS_CREATE;
// Transaction type groups for conditional field visibility.
const instrumentTxnTypes = ['PURCHASE', 'SALE', 'BONUS_SHARE', 'RIGHT_SHARE', 'SPLIT', 'MERGER', 'TRANSFER_IN', 'TRANSFER_OUT', 'CORRECTION', 'REVERSAL'];
const quantityTxnTypes = ['PURCHASE', 'SALE', 'BONUS_SHARE', 'RIGHT_SHARE', 'SPLIT', 'MERGER', 'TRANSFER_IN', 'TRANSFER_OUT', 'CORRECTION', 'REVERSAL'];
const priceTxnTypes = ['PURCHASE', 'SALE', 'RIGHT_SHARE', 'TRANSFER_IN', 'TRANSFER_OUT', 'CORRECTION', 'REVERSAL'];
export const TRANSACTION_CREATE_FORM = {
    ...formMetadata('form.transaction.create', transactionModule, transactionFeature),
    mode: 'create',
    titleKey: tk('form.transaction.createTitle'),
    descriptionKey: tk('transaction.form.description.create'),
    fields: [
        /* ── Trade Details ── */
        field('field.txnType', 'transactionType', {
            fieldType: 'select',
            options: [
                { value: 'PURCHASE', labelKey: tk('opt.txnType.PURCHASE') },
                { value: 'SALE', labelKey: tk('opt.txnType.SALE') },
                { value: 'BONUS_SHARE', labelKey: tk('opt.txnType.BONUS_SHARE') },
                { value: 'RIGHT_SHARE', labelKey: tk('opt.txnType.RIGHT_SHARE') },
                { value: 'SPLIT', labelKey: tk('opt.txnType.SPLIT') },
                { value: 'MERGER', labelKey: tk('opt.txnType.MERGER') },
                { value: 'TRANSFER_IN', labelKey: tk('opt.txnType.TRANSFER_IN') },
                { value: 'TRANSFER_OUT', labelKey: tk('opt.txnType.TRANSFER_OUT') },
                { value: 'CASH_DEPOSIT', labelKey: tk('opt.txnType.CASH_DEPOSIT') },
                { value: 'CASH_WITHDRAWAL', labelKey: tk('opt.txnType.CASH_WITHDRAWAL') },
                { value: 'FEE', labelKey: tk('opt.txnType.FEE') },
                { value: 'TAX', labelKey: tk('opt.txnType.TAX') },
                { value: 'INTEREST', labelKey: tk('opt.txnType.INTEREST') },
                { value: 'CORRECTION', labelKey: tk('opt.txnType.CORRECTION') },
                { value: 'REVERSAL', labelKey: tk('opt.txnType.REVERSAL') },
            ],
            value: {
                defaultValue: 'PURCHASE',
                hiddenValuePolicy: 'unregister',
                submitBehavior: 'include',
            },
            validation: required(),
            layout: { md: 6, order: 10 },
        }),
        field('field.transactionDate', 'transactionDate', {
            fieldType: 'date',
            validation: required(),
            layout: { md: 6, order: 20 },
        }),
        field('field.settlementDate', 'settlementDate', {
            fieldType: 'date',
            layout: { md: 6, order: 25 },
            state: { visibilityCondition: { operator: 'in', field: 'field.txnType', values: [...instrumentTxnTypes] } },
        }),
        field('field.instrumentSymbol', 'instrumentSymbol', {
            fieldType: 'select',
            optionSource: {
                adapterId: 'api',
                operationId: OPERATION_IDS.INSTRUMENT_LIST,
                valuePath: 'symbol',
                labelPath: 'instrumentName',
                cachePolicy: { ttlSeconds: 60 },
                allowSearch: true,
                minimumSearchLength: 0,
            },
            validation: required(),
            layout: { md: 6, order: 30 },
            state: { visibilityCondition: { operator: 'in', field: 'field.txnType', values: [...instrumentTxnTypes] } },
        }),
        field('field.quantity', 'quantity', {
            fieldType: 'decimal',
            validation: {
                rules: [
                    { ruleId: validationRule('required'), messageKey: tk('validation.required') },
                    {
                        ruleId: validationRule('min'),
                        params: { min: 0.000001 },
                        messageKey: tk('validation.min'),
                    },
                ],
                validateWhen: 'submit',
            },
            layout: { md: 6, order: 40 },
            state: {
                visibilityCondition: { operator: 'in', field: 'field.txnType', values: [...quantityTxnTypes] },
                requiredCondition: { operator: 'in', field: 'field.txnType', values: [...quantityTxnTypes] },
            },
        }),
        field('field.pricePerUnit', 'pricePerUnit', {
            fieldType: 'decimal',
            validation: {
                rules: [
                    { ruleId: validationRule('required'), messageKey: tk('validation.required') },
                    {
                        ruleId: validationRule('min'),
                        params: { min: 0.01 },
                        messageKey: tk('validation.min'),
                    },
                ],
                validateWhen: 'submit',
            },
            layout: { md: 6, order: 50 },
            state: { visibilityCondition: { operator: 'in', field: 'field.txnType', values: [...priceTxnTypes] } },
        }),
        field('field.currency', 'currency', {
            fieldType: 'hidden',
            value: { defaultValue: 'NPR', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        }),
        /* ── Allocation ── */
        field('field.accountId', 'clientAccountId', {
            fieldType: 'select',
            optionSource: {
                adapterId: 'api',
                operationId: OPERATION_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
                valuePath: 'id',
                labelPath: 'accountNumber',
                cachePolicy: { ttlSeconds: 60 },
                allowSearch: true,
                minimumSearchLength: 0,
            },
            validation: required(),
            layout: { md: 6, order: 60 },
        }),
        field('field.portfolioId', 'portfolioAccountId', {
            fieldType: 'select',
            optionSource: {
                adapterId: 'api',
                operationId: OPERATION_IDS.ORGANIZATION_PORTFOLIOS_LIST,
                valuePath: 'id',
                labelPath: 'name',
                cachePolicy: { ttlSeconds: 60 },
                allowSearch: true,
                minimumSearchLength: 0,
            },
            layout: { md: 6, order: 70 },
            state: { visibilityCondition: { operator: 'in', field: 'field.txnType', values: [...instrumentTxnTypes] } },
        }),
        /* ── Charges and Taxes (visible only for priced transactions) ── */
        field('field.commission', 'commission', {
            fieldType: 'decimal',
            layout: { md: 6, order: 80 },
            state: { visibilityCondition: { operator: 'in', field: 'field.txnType', values: [...priceTxnTypes] } },
        }),
        field('field.sebonFee', 'sebonFee', {
            fieldType: 'decimal',
            layout: { md: 6, order: 85 },
            state: { visibilityCondition: { operator: 'in', field: 'field.txnType', values: [...priceTxnTypes] } },
        }),
        field('field.dpCharge', 'dpCharges', {
            fieldType: 'decimal',
            layout: { md: 6, order: 90 },
            state: { visibilityCondition: { operator: 'in', field: 'field.txnType', values: [...priceTxnTypes] } },
        }),
        /* ── Source ── */
        field('field.externalReference', 'externalReference', {
            fieldType: 'text',
            layout: { md: 6, order: 100 },
        }),
        field('field.notes', 'notes', {
            fieldType: 'textarea',
            layout: { md: 12, order: 110 },
        }),
    ],
    sections: [
        {
            id: 'section.transaction.trade',
            titleKey: tk('transaction.form.section.transactionDetails'),
            fields: [
                'field.txnType',
                'field.transactionDate',
                'field.settlementDate',
                'field.instrumentSymbol',
                'field.quantity',
                'field.pricePerUnit',
            ].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
        {
            id: 'section.transaction.allocation',
            titleKey: tk('transaction.form.section.allocation'),
            fields: ['field.accountId', 'field.portfolioId'].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
        {
            id: 'section.transaction.charges',
            titleKey: tk('transaction.form.section.charges'),
            fields: ['field.commission', 'field.sebonFee', 'field.dpCharge'].map(fieldId),
            layout: { columns: 2, gap: 16 },
            visibilityCondition: { operator: 'in', field: 'field.txnType', values: [...priceTxnTypes] },
        },
        {
            id: 'section.transaction.source',
            titleKey: tk('transaction.form.section.source'),
            fields: ['field.externalReference', 'field.notes'].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
    ],
    actions: [
        localCancel(transactionModule, transactionFeature),
        {
            ...submitPlaceholder('action.transaction.create', 'cmd.investment.transactions.create', transactionModule, transactionFeature),
            labelKey: tk('action.submit'),
            successMessageKey: tk('form.transaction.success'),
            errorMessageKey: tk('form.transaction.error'),
        },
    ],
    validation: { validateWhen: 'submit' },
    submission: {
        defaultCommandId: commandId('cmd.investment.transactions.create'),
        includeFields: [
            'field.txnType',
            'field.transactionDate',
            'field.settlementDate',
            'field.instrumentSymbol',
            'field.quantity',
            'field.pricePerUnit',
            'field.currency',
            'field.accountId',
            'field.portfolioId',
            'field.commission',
            'field.sebonFee',
            'field.dpCharge',
            'field.externalReference',
            'field.notes',
        ].map(fieldId),
        successMessageKey: tk('form.transaction.success'),
        errorMessageKey: tk('form.transaction.error'),
    },
};
const importModule = MODULE_KEYS.IMPORT;
const importFeature = FEATURE_KEYS.DATA_IMPORTS;
export const IMPORT_JOB_CREATE_FORM = {
    ...formMetadata('form.importJob.create', importModule, importFeature),
    mode: 'create',
    titleKey: tk('form.importJob.title'),
    fields: [
        field('field.importJob.importType', 'importType', {
            fieldType: 'select',
            options: [
                { value: 'INSTRUMENT', labelKey: tk('importJob.type.instrument') },
                { value: 'TRANSACTION', labelKey: tk('importJob.type.transaction') },
                { value: 'MARKET_DATA', labelKey: tk('importJob.type.marketData') },
                { value: 'CORPORATE_ACTION', labelKey: tk('importJob.type.corporateAction') },
                { value: 'CUSTODY_POSITION', labelKey: tk('importJob.type.custodyPosition') },
                { value: 'OPENING_CUTOVER', labelKey: tk('importJob.type.openingCutover') },
                { value: 'TAX_RULE', labelKey: tk('importJob.type.taxRule') },
            ],
            value: {
                defaultValue: 'TRANSACTION',
                hiddenValuePolicy: 'unregister',
                submitBehavior: 'include',
            },
            validation: required(),
            layout: { md: 6 },
        }),
        field('field.importJob.sourceFileName', 'file', {
            fieldType: 'file',
            validation: required(),
            layout: { md: 12 },
        }),
        field('field.importJob.sourceSheetName', 'sourceSheetName', { layout: { md: 6 } }),
    ],
    sections: [
        {
            id: 'section.importJob.create',
            titleKey: tk('form.importJob.title'),
            fields: [
                'field.importJob.importType',
                'field.importJob.sourceFileName',
                'field.importJob.sourceSheetName',
            ].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
    ],
    actions: [
        localCancel(importModule, importFeature),
        {
            ...submitPlaceholder('action.importJob.create', 'cmd.data.importJobs.upload', importModule, importFeature),
            labelKey: tk('common:upload'),
            iconKey: 'upload',
            successMessageKey: tk('form.importJob.success'),
            errorMessageKey: tk('form.importJob.error'),
        },
    ],
    validation: { validateWhen: 'submit' },
    submission: {
        defaultCommandId: commandId('cmd.data.importJobs.upload'),
        includeFields: [
            'field.importJob.importType',
            'field.importJob.sourceFileName',
            'field.importJob.sourceSheetName',
        ].map(fieldId),
        successMessageKey: tk('form.importJob.success'),
        errorMessageKey: tk('form.importJob.error'),
    },
};
function lifecycleForm(id, mode, titleKey, fields, commandName, feature = FEATURE_KEYS.ADMIN_ORGANIZATION) {
    const writable = mode !== 'view';
    return {
        ...formMetadata(id, MODULE_KEYS.ADMIN, feature),
        mode,
        titleKey: tk(titleKey),
        fields,
        sections: [
            {
                id: `${id}.section`,
                titleKey: tk(titleKey),
                fields: fields.map((item) => item.id),
                layout: { columns: 2, gap: 16 },
            },
        ],
        actions: writable && commandName
            ? [
                localCancel(MODULE_KEYS.ADMIN, feature),
                submitPlaceholder(`${id}.submit`, commandName, MODULE_KEYS.ADMIN, feature),
            ]
            : [localCancel(MODULE_KEYS.ADMIN, feature)],
        validation: writable ? { validateWhen: 'submit' } : undefined,
        submission: writable && commandName
            ? {
                defaultCommandId: commandId(commandName),
                includeFields: fields
                    .filter((item) => item.value?.submitBehavior !== 'exclude' && !item.value?.outputOnly)
                    .map((item) => item.id),
                ...(mode === 'edit' ? { conflictReload: { labelKey: tk('common.refresh') } } : {}),
            }
            : { excludeFields: fields.map((item) => item.id) },
    };
}
const clientFields = (includeBranch) => [
    ...(includeBranch
        ? [
            field('field.client.branchId', 'branchId', {
                fieldType: 'select',
                layout: { md: 6 },
                validation: required(),
                optionSource: {
                    adapterId: 'api',
                    operationId: OPERATION_IDS.ORGANIZATION_BRANCHES_LIST,
                    valuePath: 'id',
                    labelPath: 'name',
                    allowSearch: true,
                    minimumSearchLength: 0,
                },
            }),
        ]
        : []),
    field('field.client.code', 'code', {
        layout: { md: 6 },
        value: { defaultValue: '', submitBehavior: 'include' },
    }),
    field('field.client.fullName', 'fullName', { layout: { md: 6 }, validation: required() }),
    field('field.client.email', 'email', {
        layout: { md: 6 },
        validation: {
            rules: [{ ruleId: validationRule('email'), messageKey: tk('validation.email') }],
            validateWhen: 'blur',
        },
    }),
    field('field.client.phone', 'phone', { layout: { md: 6 } }),
    field('field.client.panNumber', 'panNumber', { layout: { md: 6 } }),
    field('field.client.accountType', 'accountType', {
        fieldType: 'select',
        layout: { md: 6 },
        options: ['INDIVIDUAL', 'INSTITUTIONAL'].map((value) => ({
            value,
            labelKey: tk(`opt.clientAccountType.${value}`),
        })),
        value: { defaultValue: 'INDIVIDUAL', submitBehavior: 'include' },
    }),
    field('field.client.notes', 'notes', { fieldType: 'textarea', layout: { md: 12 } }),
];
const clientEditFields = () => [
    field('field.client.expectedUpdatedAt', 'expectedUpdatedAt', {
        fieldType: 'hidden',
        value: { hiddenValuePolicy: 'preserve', submitBehavior: 'include', retainWhenHidden: true },
    }),
    field('field.client.fullName', 'fullName', { layout: { md: 6 }, validation: required() }),
    field('field.client.email', 'email', {
        layout: { md: 6 },
        validation: {
            rules: [{ ruleId: validationRule('email'), messageKey: tk('validation.email') }],
            validateWhen: 'blur',
        },
    }),
    field('field.client.phone', 'phone', { layout: { md: 6 } }),
    field('field.client.panNumber', 'panNumber', { layout: { md: 6 } }),
    field('field.client.accountType', 'accountType', {
        fieldType: 'select',
        layout: { md: 6 },
        options: ['INDIVIDUAL', 'INSTITUTIONAL'].map((value) => ({
            value,
            labelKey: tk(`opt.clientAccountType.${value}`),
        })),
        value: { defaultValue: 'INDIVIDUAL', submitBehavior: 'include' },
    }),
    field('field.client.isActive', 'isActive', { fieldType: 'checkbox', layout: { md: 6 } }),
    field('field.client.notes', 'notes', { fieldType: 'textarea', layout: { md: 12 } }),
];
const portfolioFields = (includeParents) => [
    ...(includeParents
        ? [
            field('field.clientCustodyAccount.clientAccountId', 'clientAccountId', {
                fieldType: 'select', layout: { md: 6 }, validation: required(),
                optionSource: { adapterId: 'api', operationId: OPERATION_IDS.ORGANIZATION_CLIENTS_LIST, valuePath: 'id', labelPath: 'fullName', allowSearch: true, minimumSearchLength: 0 },
            }),
            field('field.portfolio.custodyAccountId', 'portfolioAccountId', {
                fieldType: 'select', layout: { md: 6 }, validation: required(),
                optionSource: { adapterId: 'api', operationId: OPERATION_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST, valuePath: 'id', labelPath: 'accountNumber', allowSearch: true, minimumSearchLength: 0 },
            }),
        ]
        : [
            field('field.clientCustodyAccount.expectedVersion', 'expectedVersion', {
                fieldType: 'hidden', value: { hiddenValuePolicy: 'preserve', submitBehavior: 'include', retainWhenHidden: true },
            }),
        ]),
    field('field.portfolio.name', 'name', { layout: { md: 6 }, validation: required() }),
    field('field.portfolio.baseCurrency', 'baseCurrency', {
        fieldType: 'hidden',
        value: { defaultValue: 'NPR', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
    }),
    field('field.portfolio.strategy', 'strategy', { layout: { md: 12 }, validation: required() }),
    field('field.portfolio.mandateStartDate', 'mandateStartDate', { fieldType: 'date', layout: { md: 6 }, validation: required() }),
    field('field.portfolio.mandateEndDate', 'mandateEndDate', { fieldType: 'date', layout: { md: 6 } }),
    ...(!includeParents
        ? [
            field('field.portfolio.status', 'status', { fieldType: 'select', layout: { md: 6 }, options: ['ACTIVE', 'SUSPENDED', 'CLOSED'].map((value) => ({ value, labelKey: tk(`opt.status.${value}`) })) }),
        ]
        : []),
];
const clientCustodyAccountFields = (includeClient) => [
    ...(includeClient
        ? [
            field('field.clientCustodyAccount.clientAccountId', 'clientAccountId', {
                fieldType: 'select',
                layout: { md: 6 },
                validation: required(),
                optionSource: {
                    adapterId: 'api',
                    operationId: OPERATION_IDS.ORGANIZATION_CLIENTS_LIST,
                    valuePath: 'id',
                    labelPath: 'fullName',
                    allowSearch: true,
                    minimumSearchLength: 0,
                },
            }),
        ]
        : [
            field('field.clientCustodyAccount.expectedVersion', 'expectedVersion', {
                fieldType: 'hidden',
                value: { hiddenValuePolicy: 'preserve', submitBehavior: 'include', retainWhenHidden: true },
            }),
        ]),
    field('field.clientCustodyAccount.accountNumber', 'accountNumber', {
        layout: { md: 6 },
        validation: required(),
    }),
    field('field.clientCustodyAccount.accountType', 'accountType', {
        fieldType: 'select',
        layout: { md: 6 },
        options: ['CUSTODY', 'DEMAT', 'BROKERAGE'].map((value) => ({
            value,
            labelKey: tk(`opt.clientCustodyAccountType.${value}`),
        })),
    }),
    field('field.clientCustodyAccount.baseCurrency', 'baseCurrency', {
        fieldType: 'hidden',
        value: { defaultValue: 'NPR', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
    }),
    field('field.clientCustodyAccount.isDefault', 'isDefault', {
        fieldType: 'checkbox',
        layout: { md: 6 },
    }),
    ...(!includeClient
        ? [
            field('field.clientCustodyAccount.isActive', 'isActive', {
                fieldType: 'checkbox',
                layout: { md: 6 },
            }),
        ]
        : []),
];
export const BRANCH_CREATE_FORM = lifecycleForm('form.admin.branch.create', 'create', 'form.branch.createTitle', [
    field('field.branch.code', 'code', { validation: required() }),
    field('field.branch.name', 'name', { validation: required() }),
    field('field.branch.address', 'address', {
        fieldType: 'textarea',
        layout: { md: 12 },
        helpTextKey: tk('helpText.field.branch.address'),
    }),
    field('field.branch.locale', 'locale', {
        fieldType: 'select',
        validation: required(),
        options: ['en-US', 'ne-NP'].map((value) => ({ value, labelKey: tk(`enum.locale.${value}`) })),
        value: { defaultValue: 'en-US', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
    }),
    field('field.branch.timeZone', 'timeZone', {
        fieldType: 'select',
        validation: required(),
        options: ['Asia/Kathmandu', 'Asia/Singapore', 'UTC'].map((value) => ({
            value,
            labelKey: tk(`enum.timeZone.${value.replace('/', '_')}`),
        })),
        value: {
            defaultValue: 'Asia/Kathmandu',
            hiddenValuePolicy: 'unregister',
            submitBehavior: 'include',
        },
    }),
    field('field.branch.isDefault', 'isDefault', {
        fieldType: 'checkbox',
        value: { defaultValue: false, hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
    }),
    field('field.branch.parentBranchId', 'parentBranchId', {
        fieldType: 'select',
        options: [],
        optionSource: {
            adapterId: 'experience.options.activeBranches',
            operationId: OPERATION_IDS.ORGANIZATION_BRANCHES_LIST,
            valuePath: 'id',
            labelPath: 'name',
            allowSearch: true,
            minimumSearchLength: 0,
        },
        validation: {
            rules: [
                { ruleId: validationRule('branchParentActive'), messageKey: tk('validation.branchParentActive') },
            ],
            validateWhen: 'submit',
        },
        value: { defaultValue: '', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
    }),
], 'cmd.admin.organization-branches.create');
export const PLATFORM_ORGANIZATION_CREATE_FORM = lifecycleForm('form.admin.platformOrganization.create', 'create', 'form.platformOrganization.createTitle', [
    field('field.organization.code', 'code', { validation: required() }),
    field('field.organization.name', 'name', { validation: required() }),
    field('field.organization.legalName', 'legalName', { validation: required() }),
    field('field.organization.jurisdiction', 'jurisdiction', {
        fieldType: 'hidden',
        validation: required(),
        value: { defaultValue: 'NP', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
    }),
    field('field.organization.locale', 'locale', {
        fieldType: 'select',
        validation: required(),
        options: ['en-US', 'ne-NP'].map((value) => ({ value, labelKey: tk(`enum.locale.${value}`) })),
        value: { defaultValue: 'en-US', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
    }),
    field('field.organization.timeZone', 'timeZone', {
        fieldType: 'select',
        validation: required(),
        options: ['Asia/Kathmandu', 'Asia/Singapore', 'UTC'].map((value) => ({
            value,
            labelKey: tk(`enum.timeZone.${value.replace('/', '_')}`),
        })),
        value: {
            defaultValue: 'Asia/Kathmandu',
            hiddenValuePolicy: 'unregister',
            submitBehavior: 'include',
        },
    }),
    field('field.organization.defaultBranchCode', 'defaultBranchCode', {
        validation: required(),
        value: {
            defaultValue: 'DEFAULT',
            hiddenValuePolicy: 'unregister',
            submitBehavior: 'include',
        },
    }),
    field('field.organization.defaultBranchName', 'defaultBranchName', {
        validation: required(),
        value: {
            defaultValue: 'Default Branch',
            hiddenValuePolicy: 'unregister',
            submitBehavior: 'include',
        },
    }),
    field('field.organization.modules', 'modules', {
        fieldType: 'multiselect',
        validation: required(),
        options: INVESTMENT_DOMAIN_MODULES.map((value) => ({
            value,
            labelKey: tk(`enum.module.${value}`),
        })),
    }),
    field('field.organization.baseCurrency', 'baseCurrency', {
        fieldType: 'hidden',
        value: { defaultValue: 'NPR', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
    }),
    field('field.organization.adminEmail', 'adminEmail', {
        validation: {
            rules: [
                { ruleId: validationRule('required'), messageKey: tk('validation.required') },
                { ruleId: validationRule('email'), messageKey: tk('validation.email') },
            ],
            validateWhen: 'blur',
        },
    }),
    field('field.organization.adminPassword', 'adminPassword', {
        fieldType: 'password',
        validation: {
            rules: [
                { ruleId: validationRule('required'), messageKey: tk('validation.required') },
                {
                    ruleId: validationRule('minLength'),
                    params: { min: 8 },
                    messageKey: tk('validation.minLength'),
                },
            ],
            validateWhen: 'blur',
        },
        sensitive: { payloadBehavior: 'submit', displayBehavior: 'hide' },
        value: { hiddenValuePolicy: 'clear', submitBehavior: 'include' },
    }),
], 'cmd.admin.platform-organizations.create', FEATURE_KEYS.ADMIN_ORGANIZATION);
export const PLATFORM_ORGANIZATION_EDIT_FORM = lifecycleForm('form.admin.platformOrganization.edit', 'edit', 'form.platformOrganization.editTitle', [
    field('field.organization.expectedVersion', 'expectedVersion', {
        fieldType: 'hidden',
        value: { hiddenValuePolicy: 'preserve', submitBehavior: 'include', retainWhenHidden: true },
    }),
    field('field.organization.name', 'name', { validation: required() }),
    field('field.organization.baseCurrency', 'baseCurrency', {
        fieldType: 'hidden',
        value: { defaultValue: 'NPR', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
    }),
], 'cmd.admin.platform-organizations.update', FEATURE_KEYS.ADMIN_ORGANIZATION);
// Organization profile edit form — for org-admins editing their own organization.
export const ORGANIZATION_PROFILE_EDIT_FORM = {
    ...formMetadata('form.admin.organizationProfile.edit', MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION),
    mode: 'edit',
    titleKey: tk('form.organizationProfile.editTitle'),
    fields: [
        field('field.organization.expectedVersion', 'expectedVersion', {
            fieldType: 'hidden',
            value: { hiddenValuePolicy: 'preserve', submitBehavior: 'include', retainWhenHidden: true },
        }),
        field('field.organization.name', 'name', { validation: required() }),
        field('field.organization.legalName', 'legalName'),
        field('field.organization.baseCurrency', 'baseCurrency', {
            fieldType: 'hidden',
            value: { defaultValue: 'NPR', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        }),
    ],
    sections: [
        {
            id: 'section.organizationProfile.identity',
            titleKey: tk('form.organizationProfile.section.identity'),
            fields: [
                'field.organization.expectedVersion',
                'field.organization.name',
                'field.organization.legalName',
            ].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
    ],
    actions: [
        localCancel(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION),
        submitPlaceholder('action.organizationProfile.save', 'cmd.admin.organization-profile.update', MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION),
    ],
    validation: { validateWhen: 'submit' },
    submission: {
        defaultCommandId: commandId('cmd.admin.organization-profile.update'),
        includeFields: [
            'field.organization.expectedVersion',
            'field.organization.name',
            'field.organization.legalName',
            'field.organization.baseCurrency',
        ].map(fieldId),
        successMessageKey: tk('form.organizationProfile.success'),
        errorMessageKey: tk('form.organizationProfile.error'),
    },
    operationId: OPERATION_IDS.ORGANIZATION_PROFILE_UPDATE,
};
export const ORGANIZATION_PROFILE_VIEW_FORM = {
    ...ORGANIZATION_PROFILE_EDIT_FORM,
    id: formId('form.admin.organizationProfile.view'),
    mode: 'view',
    titleKey: tk('form.organizationProfile.viewTitle'),
    fields: [
        ...ORGANIZATION_PROFILE_EDIT_FORM.fields.map((f) => String(f.id) === 'field.organization.baseCurrency'
            ? { ...f, fieldType: 'text', value: { submitBehavior: 'exclude', outputOnly: true } }
            : f),
        field('field.organization.jurisdiction', 'jurisdiction', {
            labelKey: tk('admin.profile.fields.jurisdiction'),
        }),
        field('field.organization.locale', 'locale', {
            labelKey: tk('admin.profile.fields.locale'),
        }),
        field('field.organization.timeZone', 'timeZone', {
            labelKey: tk('admin.profile.fields.timeZone'),
        }),
        field('field.organization.calendarSystem', 'calendarSystem', {
            labelKey: tk('admin.profile.fields.calendarSystem'),
        }),
        field('field.organization.status', 'status', {
            labelKey: tk('admin.profile.fields.status'),
        }),
        field('field.organization.createdAt', 'createdAt', {
            labelKey: tk('admin.profile.fields.createdAt'),
            fieldType: 'datetime',
        }),
        field('field.organization.updatedAt', 'updatedAt', {
            labelKey: tk('admin.profile.fields.updatedAt'),
            fieldType: 'datetime',
        }),
    ],
    sections: [
        {
            id: 'section.organizationProfile.identity',
            titleKey: tk('form.organizationProfile.section.identity'),
            fields: [
                'field.organization.expectedVersion',
                'field.organization.name',
                'field.organization.legalName',
                'field.organization.jurisdiction',
                'field.organization.baseCurrency',
                'field.organization.locale',
                'field.organization.timeZone',
                'field.organization.calendarSystem',
                'field.organization.status',
                'field.organization.createdAt',
                'field.organization.updatedAt',
            ].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
    ],
    actions: [localCancel(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION)],
    validation: undefined,
    submission: {
        excludeFields: [
            'field.organization.expectedVersion',
            'field.organization.name',
            'field.organization.legalName',
            'field.organization.baseCurrency',
            'field.organization.jurisdiction',
            'field.organization.locale',
            'field.organization.timeZone',
            'field.organization.calendarSystem',
            'field.organization.status',
            'field.organization.createdAt',
            'field.organization.updatedAt',
        ].map(fieldId),
    },
};
export const BRANCH_EDIT_FORM = lifecycleForm('form.admin.branch.edit', 'edit', 'form.branch.editTitle', [
    field('field.branch.expectedUpdatedAt', 'expectedUpdatedAt', {
        fieldType: 'hidden',
        value: { hiddenValuePolicy: 'preserve', submitBehavior: 'include', retainWhenHidden: true },
    }),
    field('field.branch.name', 'name', { validation: required() }),
    field('field.branch.address', 'address', {
        fieldType: 'textarea',
        layout: { md: 12 },
        helpTextKey: tk('helpText.field.branch.address'),
    }),
    field('field.branch.status', 'status', {
        fieldType: 'select',
        options: ['ACTIVE', 'INACTIVE', 'SUSPENDED'].map((value) => ({
            value,
            labelKey: tk(`opt.status.${value}`),
        })),
    }),
    field('field.branch.parentBranchId', 'parentBranchId', {
        fieldType: 'select',
        options: [],
        optionSource: {
            adapterId: 'experience.options.activeBranches',
            operationId: OPERATION_IDS.ORGANIZATION_BRANCHES_LIST,
            valuePath: 'id',
            labelPath: 'name',
            allowSearch: true,
            minimumSearchLength: 0,
        },
        validation: {
            rules: [
                { ruleId: validationRule('branchParentActive'), messageKey: tk('validation.branchParentActive') },
            ],
            validateWhen: 'submit',
        },
        value: { defaultValue: '', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
    }),
], 'cmd.admin.organization-branches.update');
export const CLIENT_CREATE_FORM = {
    ...lifecycleForm('form.admin.client.create', 'create', 'form.client.createTitle', clientFields(false), 'cmd.admin.organization-clients.create'),
    descriptionKey: tk('form.client.createDescription'),
    sections: [
        {
            id: 'section.client.profile',
            titleKey: tk('section.client.profile'),
            fields: [
                'field.client.fullName',
                'field.client.email',
                'field.client.phone',
                'field.client.panNumber',
                'field.client.accountType',
            ].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
        {
            id: 'section.client.settings',
            titleKey: tk('section.client.settings'),
            fields: ['field.client.code', 'field.client.notes'].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
    ],
    submission: {
        defaultCommandId: commandId('cmd.admin.organization-clients.create'),
        includeFields: clientFields(false).map(f => f.id),
        successMessageKey: tk('form.client.success'),
        errorMessageKey: tk('form.client.error'),
    },
};
export const CLIENT_EDIT_FORM = lifecycleForm('form.admin.client.edit', 'edit', 'form.client.editTitle', clientEditFields(), 'cmd.admin.organization-clients.update');
export const CLIENT_ACCOUNT_CREATE_FORM = lifecycleForm('form.admin.clientAccount.create', 'create', 'form.clientAccount.createTitle', clientCustodyAccountFields(true), 'cmd.admin.organization-client-accounts.create');
export const CLIENT_ACCOUNT_EDIT_FORM = lifecycleForm('form.admin.clientAccount.edit', 'edit', 'form.clientAccount.editTitle', clientCustodyAccountFields(false), 'cmd.admin.organization-client-accounts.update');
export const PORTFOLIO_CREATE_FORM = {
    ...lifecycleForm('form.admin.portfolio.create', 'create', 'form.portfolio.createTitle', portfolioFields(true), 'cmd.admin.organization-portfolios.create'),
    descriptionKey: tk('form.portfolio.createDescription'),
    sections: [
        {
            id: 'section.portfolio.ownership',
            titleKey: tk('section.portfolio.ownership'),
            fields: [
                'field.clientCustodyAccount.clientAccountId',
                'field.portfolio.custodyAccountId',
            ].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
        {
            id: 'section.portfolio.details',
            titleKey: tk('section.portfolio.details'),
            fields: [
                'field.portfolio.name',
                'field.portfolio.strategy',
                'field.portfolio.mandateStartDate',
                'field.portfolio.mandateEndDate',
                'field.portfolio.baseCurrency',
            ].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
    ],
    submission: {
        defaultCommandId: commandId('cmd.admin.organization-portfolios.create'),
        includeFields: portfolioFields(true).map(f => f.id),
        successMessageKey: tk('form.portfolio.success'),
        errorMessageKey: tk('form.portfolio.error'),
    },
};
export const PORTFOLIO_EDIT_FORM = lifecycleForm('form.admin.portfolio.edit', 'edit', 'form.portfolio.editTitle', portfolioFields(false), 'cmd.admin.organization-portfolios.update');
export const MODULE_ASSIGNMENT_EDIT_FORM = lifecycleForm('form.admin.moduleAssignment.edit', 'edit', 'form.moduleAssignments.editTitle', [
    field('field.module.expectedVersion', 'expectedVersion', {
        fieldType: 'hidden',
        value: { hiddenValuePolicy: 'preserve', submitBehavior: 'include', retainWhenHidden: true },
    }),
    field('field.module.configurationVersionId', 'configurationVersionId', { layout: { md: 12 } }),
], 'cmd.admin.organization-module-activations.update');
export const MODULE_ASSIGNMENT_CREATE_FORM = lifecycleForm('form.admin.moduleAssignment.create', 'create', 'form.moduleAssignments.createTitle', [
    field('field.module.module', 'module', {
        fieldType: 'select',
        validation: required(),
        optionSource: {
            adapterId: 'api',
            operationId: OPERATION_IDS.ORGANIZATION_MODULES_LIST_ASSIGNED,
            valuePath: 'id',
            labelPath: 'name',
            allowSearch: true,
            minimumSearchLength: 0,
        },
    }),
], 'cmd.admin.organization-module-activations.create');
function viewLifecycleForm(id, titleKey, source) {
    return lifecycleForm(id, 'view', titleKey, source.fields, undefined, source.feature);
}
export const PLATFORM_ORGANIZATION_VIEW_FORM = viewLifecycleForm('form.admin.platformOrganization.view', 'form.platformOrganization.viewTitle', PLATFORM_ORGANIZATION_CREATE_FORM);
export const BRANCH_VIEW_FORM = viewLifecycleForm('form.admin.branch.view', 'form.branch.viewTitle', BRANCH_CREATE_FORM);
export const MODULE_ASSIGNMENT_VIEW_FORM = viewLifecycleForm('form.admin.moduleAssignment.view', 'form.moduleAssignments.viewTitle', MODULE_ASSIGNMENT_CREATE_FORM);
export const CLIENT_VIEW_FORM = viewLifecycleForm('form.admin.client.view', 'form.client.viewTitle', CLIENT_CREATE_FORM);
export const CLIENT_ACCOUNT_VIEW_FORM = viewLifecycleForm('form.admin.clientAccount.view', 'form.clientAccount.viewTitle', CLIENT_ACCOUNT_CREATE_FORM);
export const PORTFOLIO_VIEW_FORM = viewLifecycleForm('form.admin.portfolio.view', 'form.portfolio.viewTitle', PORTFOLIO_CREATE_FORM);
// ── Corporate Action forms ──────────────────────────────────────────────────
const corporateActionTypes = [
    'DIVIDEND', 'INTEREST', 'BONUS', 'RIGHTS_ISSUE',
    'SPLIT', 'CONSOLIDATION', 'MERGER', 'SPINOFF',
].map((value) => ({ value, labelKey: tk(`enum.corporateActionType.${value}`) }));
function corporateActionFields(mode) {
    const view = mode === 'view';
    const readOnly = view
        ? {
            value: {
                hiddenValuePolicy: 'unregister',
                submitBehavior: 'exclude',
                outputOnly: true,
            },
        }
        : undefined;
    return [
        field('field.data.corporateAction.instrumentSymbol', 'instrumentSymbol', {
            labelKey: tk('field.instrument.symbol'),
            validation: mode === 'create' ? required() : undefined,
            state: mode === 'edit'
                ? { readonlyCondition: { operator: 'exists', field: 'field.data.corporateAction.instrumentSymbol' } }
                : undefined,
            value: mode === 'edit'
                ? { hiddenValuePolicy: 'unregister', submitBehavior: 'exclude' }
                : readOnly?.value,
        }),
        field('field.data.corporateAction.actionType', 'actionType', {
            labelKey: tk('field.corporateAction.actionType'),
            fieldType: 'select',
            options: corporateActionTypes,
            validation: mode === 'create' ? required() : undefined,
            state: mode === 'edit'
                ? { readonlyCondition: { operator: 'exists', field: 'field.data.corporateAction.actionType' } }
                : undefined,
            value: mode === 'edit'
                ? { hiddenValuePolicy: 'unregister', submitBehavior: 'exclude' }
                : readOnly?.value,
        }),
        field('field.data.corporateAction.recordDate', 'recordDate', {
            labelKey: tk('field.corporateAction.recordDate'),
            fieldType: 'date',
            validation: mode !== 'view' ? required() : undefined,
            value: readOnly?.value,
        }),
        field('field.data.corporateAction.exDate', 'exDate', {
            labelKey: tk('field.corporateAction.exDate'),
            fieldType: 'date',
            value: readOnly?.value,
        }),
        field('field.data.corporateAction.declarationDate', 'declarationDate', {
            labelKey: tk('field.corporateAction.declarationDate'),
            fieldType: 'date',
            value: readOnly?.value,
        }),
        field('field.data.corporateAction.bookCloseDate', 'bookCloseDate', {
            labelKey: tk('field.corporateAction.bookCloseDate'),
            fieldType: 'date',
            value: readOnly?.value,
        }),
        field('field.data.corporateAction.payDate', 'payDate', {
            labelKey: tk('field.corporateAction.payDate'),
            fieldType: 'date',
            value: readOnly?.value,
        }),
        field('field.data.corporateAction.fiscalYear', 'fiscalYear', {
            labelKey: tk('field.corporateAction.fiscalYear'),
            value: readOnly?.value,
        }),
        field('field.data.corporateAction.ratioNumerator', 'ratioNumerator', {
            labelKey: tk('field.corporateAction.ratioNumerator'),
            fieldType: 'number',
            value: readOnly?.value,
        }),
        field('field.data.corporateAction.ratioDenominator', 'ratioDenominator', {
            labelKey: tk('field.corporateAction.ratioDenominator'),
            fieldType: 'number',
            value: readOnly?.value,
        }),
        field('field.data.corporateAction.cashRate', 'cashRate', {
            labelKey: tk('field.corporateAction.cashRate'),
            fieldType: 'number',
            value: readOnly?.value,
        }),
        field('field.data.corporateAction.rightPrice', 'rightPrice', {
            labelKey: tk('field.corporateAction.rightPrice'),
            fieldType: 'number',
            value: readOnly?.value,
        }),
        field('field.data.corporateAction.replacementInstrumentSymbol', 'replacementInstrumentSymbol', {
            labelKey: tk('field.corporateAction.replacementInstrumentSymbol'),
            value: readOnly?.value,
        }),
        field('field.data.corporateAction.sourceReference', 'sourceReference', {
            labelKey: tk('field.corporateAction.sourceReference'),
            value: readOnly?.value,
        }),
        ...(mode === 'edit'
            ? [
                field('field.data.corporateAction.status', 'status', {
                    labelKey: tk('field.corporateAction.status'),
                    fieldType: 'select',
                    options: ['DRAFT', 'ANNOUNCED', 'PROCESSING', 'COMPLETED', 'CANCELLED'].map((value) => ({
                        value,
                        labelKey: tk(`enum.corporateActionStatus.${value}`),
                    })),
                }),
                field('field.data.corporateAction.cancellationReason', 'cancellationReason', {
                    labelKey: tk('field.corporateAction.cancellationReason'),
                }),
            ]
            : []),
    ];
}
function corporateActionForm(id, mode, command) {
    const operationId = mode === 'create' ? OPERATION_IDS.CORPORATE_ACTION_CREATE : OPERATION_IDS.CORPORATE_ACTION_UPDATE;
    return {
        ...formMetadata(id, MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS),
        mode,
        titleKey: tk(`form.corporateAction.${mode}Title`),
        fields: corporateActionFields(mode),
        sections: [
            {
                id: 'section.corporateAction.identity',
                titleKey: tk('form.corporateAction.section.identity'),
                fields: [
                    'field.data.corporateAction.instrumentSymbol',
                    'field.data.corporateAction.actionType',
                ].map(fieldId),
                layout: { columns: 2, gap: 16 },
            },
            {
                id: 'section.corporateAction.dates',
                titleKey: tk('form.corporateAction.section.dates'),
                fields: [
                    'field.data.corporateAction.recordDate',
                    'field.data.corporateAction.exDate',
                    'field.data.corporateAction.declarationDate',
                    'field.data.corporateAction.bookCloseDate',
                    'field.data.corporateAction.payDate',
                ].map(fieldId),
                layout: { columns: 2, gap: 16 },
            },
            {
                id: 'section.corporateAction.terms',
                titleKey: tk('form.corporateAction.section.terms'),
                fields: [
                    'field.data.corporateAction.fiscalYear',
                    'field.data.corporateAction.ratioNumerator',
                    'field.data.corporateAction.ratioDenominator',
                    'field.data.corporateAction.cashRate',
                    'field.data.corporateAction.rightPrice',
                    'field.data.corporateAction.replacementInstrumentSymbol',
                    'field.data.corporateAction.sourceReference',
                    ...(mode === 'edit'
                        ? [
                            'field.data.corporateAction.status',
                            'field.data.corporateAction.cancellationReason',
                        ]
                        : []),
                ].map(fieldId),
                layout: { columns: 2, gap: 16 },
            },
        ],
        actions: [
            localCancel(MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS),
            ...(command
                ? [
                    submitPlaceholder('action.data.corporateActions.submit', command, MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS),
                ]
                : []),
        ],
        submission: {
            ...(command ? { defaultCommandId: commandId(command) } : {}),
            successMessageKey: tk('form.corporateAction.success'),
            errorMessageKey: tk('form.corporateAction.error'),
        },
        operationId,
    };
}
export const CORPORATE_ACTION_CREATE_FORM = corporateActionForm('form.data.corporateAction.create', 'create', 'cmd.data.corporateActions.create');
export const CORPORATE_ACTION_EDIT_FORM = corporateActionForm('form.data.corporateAction.edit', 'edit', 'cmd.data.corporateActions.update');
export const CORPORATE_ACTION_VIEW_FORM = corporateActionForm('form.data.corporateAction.view', 'view');
export const EXPERIENCE_FORM_REGISTRY = Object.freeze({
    [String(INSTRUMENT_CREATE_FORM.id)]: INSTRUMENT_CREATE_FORM,
    [String(INSTRUMENT_EDIT_FORM.id)]: INSTRUMENT_EDIT_FORM,
    [String(INSTRUMENT_VIEW_FORM.id)]: INSTRUMENT_VIEW_FORM,
    [String(USER_CREATE_FORM.id)]: USER_CREATE_FORM,
    [String(USER_EDIT_FORM.id)]: USER_EDIT_FORM,
    [String(USER_VIEW_FORM.id)]: USER_VIEW_FORM,
    [String(ROLE_CREATE_FORM.id)]: ROLE_CREATE_FORM,
    [String(ROLE_EDIT_FORM.id)]: ROLE_EDIT_FORM,
    [String(ROLE_VIEW_FORM.id)]: ROLE_VIEW_FORM,
    [String(TRANSACTION_CREATE_FORM.id)]: TRANSACTION_CREATE_FORM,
    [String(IMPORT_JOB_CREATE_FORM.id)]: IMPORT_JOB_CREATE_FORM,
    [String(BRANCH_CREATE_FORM.id)]: BRANCH_CREATE_FORM,
    [String(BRANCH_EDIT_FORM.id)]: BRANCH_EDIT_FORM,
    [String(PLATFORM_ORGANIZATION_CREATE_FORM.id)]: PLATFORM_ORGANIZATION_CREATE_FORM,
    [String(PLATFORM_ORGANIZATION_EDIT_FORM.id)]: PLATFORM_ORGANIZATION_EDIT_FORM,
    [String(PLATFORM_ORGANIZATION_VIEW_FORM.id)]: PLATFORM_ORGANIZATION_VIEW_FORM,
    [String(ORGANIZATION_PROFILE_EDIT_FORM.id)]: ORGANIZATION_PROFILE_EDIT_FORM,
    [String(ORGANIZATION_PROFILE_VIEW_FORM.id)]: ORGANIZATION_PROFILE_VIEW_FORM,
    [String(CLIENT_CREATE_FORM.id)]: CLIENT_CREATE_FORM,
    [String(CLIENT_EDIT_FORM.id)]: CLIENT_EDIT_FORM,
    [String(CLIENT_VIEW_FORM.id)]: CLIENT_VIEW_FORM,
    [String(CLIENT_ACCOUNT_CREATE_FORM.id)]: CLIENT_ACCOUNT_CREATE_FORM,
    [String(CLIENT_ACCOUNT_EDIT_FORM.id)]: CLIENT_ACCOUNT_EDIT_FORM,
    [String(CLIENT_ACCOUNT_VIEW_FORM.id)]: CLIENT_ACCOUNT_VIEW_FORM,
    [String(PORTFOLIO_CREATE_FORM.id)]: PORTFOLIO_CREATE_FORM,
    [String(PORTFOLIO_EDIT_FORM.id)]: PORTFOLIO_EDIT_FORM,
    [String(PORTFOLIO_VIEW_FORM.id)]: PORTFOLIO_VIEW_FORM,
    [String(BRANCH_VIEW_FORM.id)]: BRANCH_VIEW_FORM,
    [String(MODULE_ASSIGNMENT_EDIT_FORM.id)]: MODULE_ASSIGNMENT_EDIT_FORM,
    [String(MODULE_ASSIGNMENT_CREATE_FORM.id)]: MODULE_ASSIGNMENT_CREATE_FORM,
    [String(MODULE_ASSIGNMENT_VIEW_FORM.id)]: MODULE_ASSIGNMENT_VIEW_FORM,
    [String(CORPORATE_ACTION_CREATE_FORM.id)]: CORPORATE_ACTION_CREATE_FORM,
    [String(CORPORATE_ACTION_EDIT_FORM.id)]: CORPORATE_ACTION_EDIT_FORM,
    [String(CORPORATE_ACTION_VIEW_FORM.id)]: CORPORATE_ACTION_VIEW_FORM,
});
export function getExperienceFormDescriptor(id) {
    return EXPERIENCE_FORM_REGISTRY[id];
}
